import {
  DataPointsApiResponse,
  getMeasureFormatString,
  createLastNWindow,
  materializeTable,
  getDefinitionWithServerOrDefaultWindow,
} from '@tableau/metrics-engine';
import { StandardMetricCard } from '@tableau/metrics-shared-widgets-dom';
import * as React from 'react';
import * as _ from 'underscore';
import { CardViewStyles } from '.././CardViewStyles';
import { ContentCard, ContentCardProps, getContentUrlForItem } from '../ContentCard';
import { Thumbnail } from '../Thumbnail';
import { imgPath } from '../../../../common/utils/ImgUrl';
import { L10n } from '../../../../common/utils/L10n';
import { Metric, SearchResultItem } from '../../../models/SearchResultTypes';
import { Messages } from '../../../../../gen-src/Localize';
import { isMetricInfo } from '../../../models/IMetricInfo';
import { MetricCardProps as StandardMetricCardProps } from '@tableau/metrics-shared-widgets-dom';
import { CommonStyles } from '../../../../common/styles/CommonStyles';
import { FeatureFlag, FeatureFlagService } from '../../../services/FeatureFlagService';
import { MetricTooltip } from '../tooltips/MetricTooltip';
import { SessionManagementService } from '../../../services/SessionManagementService';
import { EntityLink, UserEntityLink } from '../../tooltips/elements/EntityLink';
import { ConnectedFavoriteToggle } from '../../Favorites/FavoriteToggle';

interface MetricCardState {
  readonly metricData?: DataPointsApiResponse;
}

// eslint-disable-next-line prettier/prettier
export interface MetricCardOwnProps extends ContentCardProps { }

export interface MetricCardStateProps {
  readonly metricData?: DataPointsApiResponse | undefined;
}

export type MetricCardProps = MetricCardOwnProps & MetricCardStateProps;

export const MetricDefaultImageUrl = imgPath('Spaces_Empty_Metrics.svg');

export class MetricCard extends ContentCard<MetricCardProps, MetricCardState> {
  public state: MetricCardState = {};

  private renderTimerHandle?: ReturnType<typeof setTimeout>;

  public render() {
    const content = this.props.content as Metric;
    const isOwner = SessionManagementService.instance.sessionUserId() === content.owner?.id;
    const locationLink = this.getLinkForLocationType(this.props.urls, isOwner, content.location);

    return (
      <div className={CardViewStyles.Card}>
        {this.renderMetricCard()}
        <div className={CardViewStyles.ActionRow} style={{ marginTop: CommonStyles.Spacing.LargeSpace }}>
          <span className={CardViewStyles.ActionRowName} title={content.name}>
            {L10n.elideIfLong(content.name)}
          </span>
          {FeatureFlagService.instance.getValue(FeatureFlag.offspringPostMVP224) ? (
            <span style={{ position: 'relative', bottom: '2px' }}>
              <ConnectedFavoriteToggle searchResultItem={content} />
            </span>
          ) : null}
          {this.renderInfoIconAndPopover(() => (
            <MetricTooltip
              content={content}
              urls={this.props.urls}
              isOwner={isOwner}
              refreshedAt={this.props.metricData?.dataRefreshedTimestamp}
              hasMetricData={!!this.props.metricData?.data}
            />
          ))}
          {/* {this.renderActionsMenu()} */}
        </div>
        <div>
          {content.owner && (
            <div className={CardViewStyles.SubSection}>
              {Messages.OwnerSubTitle()}{' '}
              <b>
                <UserEntityLink
                  name={content.owner.displayName}
                  href={this.props.urls.userContentUrl(content.owner.username, content.owner.domainName)}
                  styleOverride={CardViewStyles.Links}
                />
              </b>
            </div>
          )}

          {content.location?.type ? (
            locationLink.url ? (
              <div className={CardViewStyles.SubSection}>
                {Messages.LocationSubTitle()}{' '}
                <b>{<EntityLink name={locationLink.text} href={locationLink.url} styleOverride={CardViewStyles.Links} />}</b>
              </div>
            ) : (
              <div className={CardViewStyles.SubSection}>
                {Messages.LocationSubTitle()} <b>{locationLink.text}</b>
              </div>
            )
          ) : (
            <></>
          )}
        </div>
      </div>
    );
  }

  public componentDidUpdate(): void {
    this.delayAddingDataToState(this.state, this.props.content);
  }

  public componentDidMount(): void {
    this.delayAddingDataToState(this.state, this.props.content);
  }

  public componentWillUnmount() {
    if (this.renderTimerHandle) {
      clearTimeout(this.renderTimerHandle);
    }
  }

  private delayAddingDataToState(state: MetricCardState, content: SearchResultItem): void {
    if (isMetricInfo(content) && this.props.metricData !== state.metricData) {
      this.renderTimerHandle = setTimeout(() => this.setState({ metricData: this.props.metricData }), Math.floor(Math.random() * 10));
    }
  }

  private renderMetricCard(): JSX.Element {
    if (this.haveMetricDataPoints()) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return renderMetricCardContent(this.state.metricData!, this.props);
    } else {
      return (
        <Thumbnail
          defaultImageUrl={MetricDefaultImageUrl}
          // TBD hoveredOrSelected={this.props.hoveredOrSelected}
          navigateUrl={getContentUrlForItem(this.props.content)}
          useBase64EncodedImage={true}
          onResultItemSelected={this.props.onResultItemSelected}
          ariaLabel={this.props.content.name}
        />
      );
    }
  }

  private haveMetricDataPoints(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.state.metricData! && !_.isUndefined(this.state.metricData!.data);
  }
}

// This is a separate function so other card components can reuse the metric thumbnail
export function renderMetricCardContent(metricData: DataPointsApiResponse, contentCardProps: ContentCardProps): JSX.Element {
  const containerClasses = [CardViewStyles.ThumbnailPreloaded];
  /* TBD const containerClasses = contentCardProps.hoveredOrSelected ? [CardViewStyles.ThumbnailPreloaded, CardViewStyles.CardHighlight]
    : [CardViewStyles.ThumbnailPreloaded]; */

  const metricCardProperties = metricCardProps(metricData);
  /* TBD if (contentCardProps.forDragPreview) {
    const measureAccessor = buildMeasureAccessor(metricCardProperties.definition);
    // when rendering the card just for a drag preview, don't show the sparkline because it makes the preview slower
    metricCardProperties.data = [getLatestValue(metricCardProperties.data, measureAccessor)];
  } */
  return (
    <div>
      <div className={containerClasses.join(' ')}>
        <div></div>
        <a
          href={getContentUrlForItem(contentCardProps.content)}
          style={{ textDecoration: 'none' }}
          onClick={contentCardProps.onResultItemSelected}
          className={'thumbnail-link'}
          aria-label={contentCardProps.content.name}
        >
          {<StandardMetricCard {...metricCardProperties} />}
        </a>
      </div>
    </div>
  );
}

const metricCardProps = (metric: DataPointsApiResponse): StandardMetricCardProps => {
  const data = metric.data ? metric.data : [];
  const measureFormat = getMeasureFormatString(metric.definition);
  const definition = getDefinitionWithServerOrDefaultWindow(
    metric.definition,
    createLastNWindow(100),
    FeatureFlagService.instance.getValue(FeatureFlag.metricsDateWindowEnabled)
  );

  const materializedData = materializeTable(definition, data);
  const dataToRender = materializedData.unwrapOr([]);

  return {
    testId: 'metric-card',
    data: dataToRender,
    definition,
    lastUpdatedTimestamp: metric.dataRefreshedTimestamp,
    measureFormat,
    suspended: false,
    metricColorIndicatorsEnabled: FeatureFlagService.instance.getValue(FeatureFlag.metricsColorIndicatorsEnabled),
    metricsReferenceLineEnabled: FeatureFlagService.instance.getValue(FeatureFlag.metricsReferenceLineEnabled),
  };
};
